import type { ErrorLike } from '@apollo/client';
import {
  CombinedGraphQLErrors,
  type ServerError,
  type ServerParseError
} from '@apollo/client/errors';
import type { FunctionComponentWithChildren } from '@bonsai-components/utility-types';
import { ModAlert, ModButton } from '@moderneinc/design-system-components';
import { List, ListItem, Typography } from '@mui/material';
import { logError } from '../../../helpers/logger.helper';
import { ReplayIcon } from '../../../icons/icons';

const isAServerOrServerParseError = (
  error: Error | ServerError | ServerParseError | null
): error is ServerError | ServerParseError => {
  return isAServerError(error) || isAServerParseError(error);
};
export const isAServerError = (
  error: Error | ServerError | ServerParseError | null
): error is ServerError => {
  return (error as ServerError)?.statusCode !== undefined;
};

const isAServerParseError = (
  error: Error | ServerError | ServerParseError | null
): error is ServerParseError => {
  return (error as ServerParseError)?.bodyText !== undefined;
};

const isNotFoundError = (error: ErrorLike): boolean => {
  return (
    error?.message.toLowerCase().includes('not found') ||
    error?.message.toLowerCase().includes('not find') ||
    (isAServerOrServerParseError(error) && error.statusCode === 404)
  );
};

const isUnauthorizedError = (error: ErrorLike): boolean => {
  return (
    error?.message.toLowerCase().includes('unauthorized') ||
    error?.message.toLowerCase().includes('access denied') ||
    (isAServerOrServerParseError(error) && error.statusCode === 401)
  );
};

const isUnavailableError = (error: ErrorLike): boolean => {
  return (
    error?.message.toLowerCase().includes('econnrefused') ||
    (isAServerOrServerParseError(error) && error.statusCode === 500)
  );
};

const isDataIntegrityError = (error: ErrorLike): boolean => {
  return error?.message.toLowerCase().includes('declared as a non null type');
};

const isConnectorUnavailable = (error: ErrorLike): boolean => {
  const msg = error?.message.toLowerCase();
  return (
    msg?.includes('no matching agents') ||
    msg?.includes('no connected connector')
  );
};

const isInternalServerError = (error: ErrorLike): boolean => {
  return error?.message.toLowerCase().includes('internal server error');
};
const isScmExchangeError = (error: ErrorLike): boolean => {
  return error?.message.toLowerCase().includes('exchange');
};

const getFriendlyErrorMessage = (error: ErrorLike): string => {
  if (isScmExchangeError(error)) {
    return error.message;
  } else if (isUnauthorizedError(error)) {
    return 'You are not authorized to perform this action.';
  } else if (isDataIntegrityError(error) && !isConnectorUnavailable(error)) {
    return 'We encountered an issue with retrieving your request.';
  } else if (isUnavailableError(error)) {
    return 'Please wait while we attempt to reconnect.';
  } else if (isNotFoundError(error)) {
    return 'Unable to find the requested resource.';
  } else if (
    isInternalServerError(error) ||
    (error &&
      'networkError' in error &&
      isAServerOrServerParseError(
        (error as Record<string, unknown>).networkError as
          | ServerError
          | ServerParseError
      ) &&
      (
        (error as Record<string, unknown>).networkError as
          | ServerError
          | ServerParseError
      )?.statusCode === 400)
  ) {
    return 'Looks like we could not connect to our sources to find what you were looking for.';
  } else if (isConnectorUnavailable(error)) {
    return 'We were unable to connect to or find a connector required to display this content.';
  } else {
    return error?.message || 'An unknown error has occurred.';
  }
};

const getErrorTitle = (error: ErrorLike): string => {
  if (isScmExchangeError(error)) {
    return 'Unable to authorize with your SCM provider.';
  } else if (isUnauthorizedError(error)) {
    return 'Unauthorized';
  } else if (isDataIntegrityError(error) && !isConnectorUnavailable(error)) {
    return 'Unable to find the data you requested';
  } else if (isNotFoundError(error)) {
    return 'Not found';
  } else if (isUnavailableError(error)) {
    return 'Unavailable';
  } else if (
    isInternalServerError(error) ||
    isAServerOrServerParseError(error)
  ) {
    return 'Network error';
  } else if (isConnectorUnavailable(error)) {
    return 'Trouble connecting to connector';
  } else {
    return 'GraphQL error';
  }
};

export const ModerneGraphQLError: FunctionComponentWithChildren<{
  error: ErrorLike | CombinedGraphQLErrors;
  refetch?: () => unknown;
}> = ({ error, children = null, refetch }) => {
  logError(error);
  return error ? (
    <ModAlert
      intent="error"
      title={getErrorTitle(error)}
      action={
        refetch ? (
          <ModButton
            startIcon={<ReplayIcon />}
            variant="primary"
            size="small"
            onClick={() => refetch()}
          >
            Retry
          </ModButton>
        ) : undefined
      }
    >
      {CombinedGraphQLErrors.is(error) && error.errors?.length > 0 ? (
        <List>
          {error.errors.map((err, idx) => (
            <ListItem key={idx}>
              <Typography variant="bodySm">{err.message}</Typography>
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="bodySm">
          {children || getFriendlyErrorMessage(error)}
        </Typography>
      )}
    </ModAlert>
  ) : null;
};
